import { useEffect, useState } from "react";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_OPTIONS = ["pending", "confirmed", "declined"];

const statusBadgeClass = (status) =>
  ({
    pending: "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/30",
    confirmed: "bg-[#4ADE80]/10 text-[#4ADE80] border-[#4ADE80]/30",
    declined: "bg-[#F87171]/10 text-[#F87171] border-[#F87171]/30",
  }[status] || "bg-white/10 text-white border-white/20");

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-[#141414] p-8" data-testid="admin-login-form">
      <p className="eyebrow mb-2">Owner access</p>
      <h1 className="font-display text-3xl text-[#F5F5F5] mb-8">Booking inbox</h1>
      <div className="space-y-5">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Email</Label>
          <Input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-[#0C0C0C] border-white/10 text-[#F5F5F5] rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Password</Label>
          <Input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 bg-[#0C0C0C] border-white/10 text-[#F5F5F5] rounded-sm" />
        </div>
        <Button type="submit" disabled={busy} data-testid="admin-login-button" className="w-full rounded-sm bg-[#D98B48] hover:bg-[#F0A562] text-[#090909] font-medium h-12">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
      </div>
    </form>
  );
}

function Bookings({ onLogout }) {
  const [bookings, setBookings] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setRefreshing(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status });
      setBookings((b) => b.map((x) => (x.id === id ? data : x)));
      toast.success(`Booking marked ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="w-full max-w-6xl" data-testid="admin-bookings-table">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="eyebrow mb-2">Owner access</p>
          <h1 className="font-display text-4xl text-[#F5F5F5]">Booking inbox</h1>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" data-testid="admin-refresh-button" onClick={load} className="rounded-sm border-white/20 bg-transparent text-[#F5F5F5] hover:bg-white/10">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" size="sm" data-testid="admin-logout-button" onClick={onLogout} className="rounded-sm border-white/20 bg-transparent text-[#F5F5F5] hover:bg-white/10">
            <LogOut className="h-4 w-4" /> Log out
          </Button>
        </div>
      </div>

      {bookings === null ? (
        <div className="py-24 text-center text-[#A3A3A3] text-sm">Loading bookings…</div>
      ) : bookings.length === 0 ? (
        <div className="border border-dashed border-white/15 py-24 text-center" data-testid="admin-empty-state">
          <p className="font-display text-2xl text-[#F5F5F5]">No bookings yet.</p>
          <p className="mt-2 text-sm text-[#A3A3A3] font-light">Booking requests will appear here the moment a customer submits one.</p>
        </div>
      ) : (
        <div className="border border-white/10 rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[#A3A3A3]">Received</TableHead>
                <TableHead className="text-[#A3A3A3]">Customer</TableHead>
                <TableHead className="text-[#A3A3A3]">Package</TableHead>
                <TableHead className="text-[#A3A3A3]">Event</TableHead>
                <TableHead className="text-[#A3A3A3]">Guests</TableHead>
                <TableHead className="text-[#A3A3A3]">Deposit</TableHead>
                <TableHead className="text-[#A3A3A3]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.map((b) => (
                <TableRow key={b.id} className="border-white/10" data-testid={`admin-booking-row-${b.id}`}>
                  <TableCell className="text-xs text-[#A3A3A3] whitespace-nowrap">{b.created_at.slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="text-sm text-[#F5F5F5]">{b.name}</div>
                    <div className="text-xs text-[#A3A3A3]">{b.email}</div>
                  </TableCell>
                  <TableCell className="text-sm text-[#F5F5F5] whitespace-nowrap">{b.package_name}</TableCell>
                  <TableCell className="text-sm text-[#F5F5F5] whitespace-nowrap">
                    {b.event_date} · {b.event_time}
                    <div className="text-xs text-[#A3A3A3] max-w-[200px] truncate">{b.venue}</div>
                  </TableCell>
                  <TableCell className="text-sm text-[#F5F5F5]">{b.guest_count}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={b.payment_status === "deposit_paid" ? statusBadgeClass("confirmed") : "bg-white/5 text-[#A3A3A3] border-white/20"}>
                      {b.payment_status === "deposit_paid" ? `$${b.deposit_amount} paid` : "unpaid"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                      <SelectTrigger data-testid={`admin-status-select-${b.id}`} className="w-[130px] h-9 bg-[#0C0C0C] border-white/10 text-sm rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141414] border-white/10 rounded-sm">
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);

  const check = () => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));

  useEffect(() => {
    check();
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    setUser(null);
  };

  return (
    <main className="min-h-screen bg-[#090909] flex flex-col items-center justify-center px-5 py-16">
      {user === undefined && <Loader2 className="h-8 w-8 animate-spin text-[#D98B48]" data-testid="admin-loading" />}
      {user === null && (
        <div className="w-full flex flex-col items-center gap-8">
          <Logo />
          <Login onSuccess={check} />
        </div>
      )}
      {user && <Bookings onLogout={logout} />}
    </main>
  );
}
